import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useMessage } from "../contexts/MessageContext";
import { Loading } from "./common/Loading";

const apiUrl =
  window._env_?.REACT_APP_API_URL ||
  "https://app.mylocaleventrentals.com/api";

const emptyItem = () => ({ inventoryId: "", quantity: 1, price: 0, name: "", description: "" });
const emptyProduct = () => ({ name: "", description: "", price: "", quantity: 1 });

const OrderForm = () => {
  const { uuid, customerId } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { showSuccess, showError, showWarning } = useMessage();
  const token = localStorage.getItem("token");

  const isEdit = Boolean(uuid);
  const isCustomerOrder = Boolean(customerId);
  const productSearchRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [order, setOrder] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerList, setCustomerList] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [newCustomer, setNewCustomer] = useState({ firstname: "", lastname: "", email: "", phone: "" });
  const [itemQueries, setItemQueries] = useState({});
  const [activeItemIndex, setActiveItemIndex] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [productTargetIndex, setProductTargetIndex] = useState(null);
  const [newProduct, setNewProduct] = useState(emptyProduct());
  const [savingProduct, setSavingProduct] = useState(false);
  const [formData, setFormData] = useState({
    customerId: customerId || null,
    status: "pending",
    startDate: "",
    endDate: "",
    items: [emptyItem()],
  });

  useEffect(() => {
    if (!token) {
      logout();
      navigate("/login");
      return;
    }

    const init = async () => {
      try {
        const inventoryItems = await fetchInventory();
        if (isEdit) {
          await fetchOrder(inventoryItems);
        } else if (isCustomerOrder) {
          await fetchCustomer(customerId);
          setFormData((prev) => ({ ...prev, customerId }));
        }
      } catch (err) {
        console.error("Order form load failed:", err);
        showError("Failed to load order form.");
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [uuid, customerId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!customerQuery || selectedCustomer || isEdit || isCustomerOrder) {
      setCustomerList([]);
      return;
    }

    const fetchCustomers = async () => {
      try {
        const res = await axios.get(`${apiUrl}/customers/search?q=${encodeURIComponent(customerQuery)}`, {
          headers: { "x-auth-token": token },
        });
        setCustomerList(res.data?.data || []);
      } catch (err) {
        console.error("Customer search failed:", err);
      }
    };

    fetchCustomers();
  }, [customerQuery, selectedCustomer, isEdit, isCustomerOrder, token]);

  useEffect(() => {
    if (!formData.startDate || formData.endDate) return;
    const start = new Date(formData.startDate);
    start.setDate(start.getDate() + 1);
    setFormData((prev) => ({ ...prev, endDate: start.toISOString().split("T")[0] }));
  }, [formData.startDate, formData.endDate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (productSearchRef.current && !productSearchRef.current.contains(event.target)) {
        setActiveItemIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchInventory = async () => {
    const res = await axios.get(`${apiUrl}/inventory`, {
      headers: { "x-auth-token": token },
    });
    const items = (res.data.items || []).sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" })
    );
    setInventory(items);
    return items;
  };

  const fetchCustomer = async (id) => {
    const res = await axios.get(`${apiUrl}/customers/${id}`, {
      headers: { "x-auth-token": token },
    });
    const customer = res.data?.data;
    setSelectedCustomer(customer);
    if (customer) setCustomerQuery(`${customer.firstname} ${customer.lastname}`.trim());
  };

  const fetchOrder = async (inventoryItems = inventory) => {
    const res = await axios.get(`${apiUrl}/order/${uuid}`, {
      headers: { "x-auth-token": token },
    });
    const orderData = res.data;
    const lines = (orderData.items || []).map((item) => {
      const inventoryItem = item.inventory || inventoryItems.find((i) => i.uuid === item.inventoryId) || {};
      return {
        inventoryId: inventoryItem.uuid || item.inventoryId || "",
        quantity: item.quantity || 1,
        price: item.price || inventoryItem.price || 0,
        name: inventoryItem.name || "",
        description: item.description || "",
      };
    });

    setOrder(orderData);
    setSelectedCustomer(orderData.customer);
    if (orderData.customer) {
      setCustomerQuery(`${orderData.customer.firstname} ${orderData.customer.lastname}`.trim());
    }
    setItemQueries(
      lines.reduce((acc, line, index) => {
        acc[index] = line.name;
        return acc;
      }, {})
    );
    setFormData({
      customerId: orderData.customerId,
      status: orderData.status || "pending",
      startDate: orderData.startDate?.split("T")[0] || "",
      endDate: orderData.endDate?.split("T")[0] || "",
      items: lines.length ? lines : [emptyItem()],
    });
  };

  const filteredInventory = (query) => {
    const term = String(query || "").trim().toLowerCase();
    if (!term) return inventory.slice(0, 8);
    return inventory
      .filter((item) =>
        [item.name, item.description, item.category?.name]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term))
      )
      .slice(0, 8);
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setCustomerQuery(`${customer.firstname} ${customer.lastname}`.trim());
    setCustomerList([]);
    setFormData((prev) => ({ ...prev, customerId: customer.uuid }));
  };

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData((prev) => {
      const items = prev.items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      );
      return { ...prev, items };
    });
  };

  const selectInventoryItem = (index, inventoryItem) => {
    setFormData((prev) => {
      const items = prev.items.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              inventoryId: inventoryItem.uuid,
              name: inventoryItem.name,
              price: Number(inventoryItem.price) || 0,
            }
          : item
      );
      return { ...prev, items };
    });
    setItemQueries((prev) => ({ ...prev, [index]: inventoryItem.name }));
    setActiveItemIndex(null);
  };

  const addItem = () => {
    setFormData((prev) => ({ ...prev, items: [...prev.items, emptyItem()] }));
  };

  const removeItem = (index) => {
    setFormData((prev) => {
      const items = prev.items.filter((_, itemIndex) => itemIndex !== index);
      return { ...prev, items: items.length ? items : [emptyItem()] };
    });
    setItemQueries((prev) => {
      const next = {};
      Object.values(prev)
        .filter((_, itemIndex) => itemIndex !== index)
        .forEach((query, itemIndex) => {
          next[itemIndex] = query;
        });
      return next;
    });
  };

  const openProductModal = (index, name = "") => {
    setProductTargetIndex(index);
    setNewProduct({ ...emptyProduct(), name });
    setShowProductModal(true);
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    if (!newProduct.name.trim()) {
      showWarning("Enter a product name before saving.");
      return;
    }

    try {
      setSavingProduct(true);
      const payload = {
        name: newProduct.name.trim(),
        description: newProduct.description.trim() || null,
        price: Number(newProduct.price) || 0,
        quantity: Math.max(0, Number(newProduct.quantity) || 0),
      };
      const res = await axios.post(`${apiUrl}/inventory/add`, payload, {
        headers: { "x-auth-token": token },
      });
      const createdItem = res.data?.data || res.data;
      setInventory((prev) =>
        [...prev, createdItem].sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" })
        )
      );
      const targetIndex = productTargetIndex ?? Math.max(formData.items.length - 1, 0);
      selectInventoryItem(targetIndex, createdItem);
      setShowProductModal(false);
      setNewProduct(emptyProduct());
      showSuccess("Product created and selected.");
    } catch (err) {
      console.error("Product creation failed:", err);
      showError(err.response?.data?.message || "Failed to create product.");
    } finally {
      setSavingProduct(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validItems = formData.items.filter((item) => item.inventoryId && Number(item.quantity) > 0);
    if (validItems.length === 0) {
      showWarning("Add at least one product before saving the order.");
      return;
    }

    setSaving(true);
    let finalCustomerId = selectedCustomer?.uuid || formData.customerId;

    if (!isEdit && !finalCustomerId) {
      try {
        const resCustomer = await axios.post(
          `${apiUrl}/customers/new`,
          {
            firstname: newCustomer.firstname,
            lastname: newCustomer.lastname,
            email: newCustomer.email,
            phone: newCustomer.phone,
          },
          { headers: { "x-auth-token": token } }
        );
        finalCustomerId = resCustomer.data.data.uuid;
      } catch (err) {
        console.error("Customer creation failed", err.response?.data || err);
        showError(err.response?.data?.message || "Failed to create customer.");
        setSaving(false);
        return;
      }
    }

    try {
      if (isEdit) {
        await axios.put(
          `${apiUrl}/order/${uuid}`,
          {
            status: formData.status,
            startDate: formData.startDate,
            endDate: formData.endDate,
          },
          { headers: { "x-auth-token": token } }
        );
        await axios.put(
          `${apiUrl}/order/${uuid}/items`,
          { items: validItems },
          { headers: { "x-auth-token": token } }
        );
        showSuccess("Order updated successfully.");
        navigate(`/orders/${uuid}`);
      } else {
        const res = await axios.post(
          `${apiUrl}/order`,
          { ...formData, customerId: finalCustomerId, items: validItems },
          { headers: { "x-auth-token": token } }
        );
        showSuccess("Order created successfully.");
        navigate(`/orders/${res.data.orderId}`);
      }
    } catch (err) {
      console.error(err);
      showError(err.response?.data?.error || "Failed to save order.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(isEdit ? `/orders/${uuid}` : "/orders");
  };

  const totalAmount = useMemo(
    () => formData.items.reduce((sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.price) || 0), 0),
    [formData.items]
  );

  if (loading) return <Loading text="Loading order..." />;
  if (isEdit && !order) return <div className="p-6">Order not found</div>;

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-blue-700">Orders Management</p>
            <h2 className="text-2xl font-bold text-gray-900 md:text-3xl">
              {isEdit ? `Edit Order${order?.formattedOrderNumber ? `: ${order.formattedOrderNumber}` : ""}` : "New Order"}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {isEdit ? "Update customer order details and line items." : "Choose a customer, rental dates, and products for this order."}
            </p>
          </div>
          <button
            type="button"
            onClick={handleCancel}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {isEdit ? "Back to Order" : "Back to Orders"}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Customer Details</h3>
              <p className="text-sm text-gray-500">
                {selectedCustomer ? "This order is linked to the customer below." : "Search for an existing customer or enter a new one."}
              </p>
            </div>

            {!selectedCustomer && !isEdit && !isCustomerOrder && (
              <div className="relative mb-4">
                <label className="mb-1 block text-sm font-medium text-gray-700">Search Customer</label>
                <input
                  type="text"
                  value={customerQuery}
                  onChange={(e) => {
                    setCustomerQuery(e.target.value);
                    setSelectedCustomer(null);
                    setFormData((prev) => ({ ...prev, customerId: null }));
                  }}
                  placeholder="Start typing a name, phone, or email..."
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
                {customerList.length > 0 && (
                  <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                    {customerList.map((customer) => (
                      <li
                        key={customer.uuid}
                        onClick={() => handleCustomerSelect(customer)}
                        className="cursor-pointer border-b border-gray-100 px-3 py-2 last:border-b-0 hover:bg-blue-50"
                      >
                        <div className="font-medium text-gray-900">{customer.firstname} {customer.lastname}</div>
                        <div className="text-xs text-gray-500">{customer.email || "No email"}{customer.phone ? ` • ${customer.phone}` : ""}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {selectedCustomer ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <input readOnly value={selectedCustomer.firstname || ""} className="rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700" />
                <input readOnly value={selectedCustomer.lastname || ""} className="rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700" />
                <input readOnly value={selectedCustomer.email || ""} className="rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700" />
                <input readOnly value={selectedCustomer.phone || ""} className="rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700" />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <input type="text" placeholder="First Name *" value={newCustomer.firstname} onChange={(e) => setNewCustomer({ ...newCustomer, firstname: e.target.value })} className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" required={!isEdit} />
                <input type="text" placeholder="Last Name *" value={newCustomer.lastname} onChange={(e) => setNewCustomer({ ...newCustomer, lastname: e.target.value })} className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" required={!isEdit} />
                <input type="email" placeholder="Email *" value={newCustomer.email} onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })} className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" required={!isEdit} />
                <input type="text" placeholder="Phone" value={newCustomer.phone} onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })} className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
              </div>
            )}
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900">Order Details</h3>
            <p className="mb-4 text-sm text-gray-500">Set the order status and rental date range.</p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Order Status</label>
                <select name="status" value={formData.status} onChange={handleChange} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="delivered">Delivered</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Order Start Date</label>
                <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} required className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Order End Date</label>
                <input type="date" name="endDate" value={formData.endDate} onChange={handleChange} required className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm" ref={productSearchRef}>
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Line Items</h3>
                <p className="text-sm text-gray-500">Search inventory by typing, or create a missing product in place. Add optional line notes (e.g. delivery address and time).</p>
              </div>
              <button type="button" onClick={addItem} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
                + Add Line
              </button>
            </div>

            <div className="overflow-x-auto rounded-md border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-3 text-left">Product</th>
                    <th className="min-w-[12rem] px-4 py-3 text-left">Description</th>
                    <th className="w-28 px-4 py-3 text-right">Qty</th>
                    <th className="w-32 px-4 py-3 text-right">Unit Price</th>
                    <th className="w-32 px-4 py-3 text-right">Total</th>
                    <th className="w-16 px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {formData.items.map((item, index) => {
                    const query = itemQueries[index] ?? item.name ?? "";
                    const matches = filteredInventory(query);
                    const showDropdown = activeItemIndex === index;
                    return (
                      <tr key={index} className="align-top">
                        <td className="relative px-4 py-3">
                          <input
                            type="text"
                            value={query}
                            onFocus={() => setActiveItemIndex(index)}
                            onChange={(e) => {
                              const value = e.target.value;
                              setActiveItemIndex(index);
                              setItemQueries((prev) => ({ ...prev, [index]: value }));
                              handleItemChange(index, "inventoryId", "");
                              handleItemChange(index, "name", value);
                            }}
                            placeholder="Start typing to search products..."
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                            required
                          />
                          {showDropdown && (
                            <div className="absolute left-4 right-4 z-30 mt-1 max-h-72 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                              {matches.length > 0 ? (
                                matches.map((inventoryItem) => (
                                  <button
                                    key={inventoryItem.uuid}
                                    type="button"
                                    onClick={() => selectInventoryItem(index, inventoryItem)}
                                    className="block w-full border-b border-gray-100 px-3 py-2 text-left last:border-b-0 hover:bg-blue-50"
                                  >
                                    <div className="font-medium text-gray-900">{inventoryItem.name}</div>
                                    <div className="text-xs text-gray-500">
                                      ${Number(inventoryItem.price || 0).toFixed(2)} • Stock: {inventoryItem.quantity ?? 0}
                                      {inventoryItem.category?.name ? ` • ${inventoryItem.category.name}` : ""}
                                    </div>
                                  </button>
                                ))
                              ) : (
                                <div className="p-3">
                                  <p className="text-sm text-gray-500">No products found for "{query}".</p>
                                  <button
                                    type="button"
                                    onClick={() => openProductModal(index, query.trim())}
                                    className="mt-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
                                  >
                                    Add "{query}" as a product
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <textarea
                            value={item.description}
                            onChange={(e) => handleItemChange(index, "description", e.target.value)}
                            rows={2}
                            placeholder="Optional notes (e.g. delivery address, time window)"
                            className="w-full min-w-[12rem] rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <input type="number" min="1" value={item.quantity} onChange={(e) => handleItemChange(index, "quantity", Number(e.target.value) || 1)} className="w-20 rounded-md border border-gray-300 px-2 py-2 text-right text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <input type="number" min="0" step="0.01" value={item.price} onChange={(e) => handleItemChange(index, "price", Number(e.target.value) || 0)} className="w-28 rounded-md border border-gray-300 px-2 py-2 text-right text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-900">
                          ${((Number(item.quantity) || 0) * (Number(item.price) || 0)).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button type="button" onClick={() => removeItem(index)} className="text-lg leading-none text-red-500 hover:text-red-700" aria-label="Remove line">
                            &times;
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td colSpan={4} className="px-4 py-3 text-right font-semibold text-gray-700">Total</td>
                    <td className="px-4 py-3 text-right text-lg font-bold text-blue-700">${totalAmount.toFixed(2)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button type="button" className="rounded-md border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50" onClick={handleCancel}>
              Cancel
            </button>
            <button type="submit" disabled={saving} className="rounded-md bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60">
              {saving ? "Saving..." : isEdit ? "Update Order" : "Save Order"}
            </button>
          </div>
        </form>
      </div>

      {showProductModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
            <div className="border-b border-gray-200 px-5 py-4">
              <h3 className="text-lg font-semibold text-gray-900">Create Product</h3>
              <p className="mt-1 text-sm text-gray-500">Add a new inventory item and select it for this line.</p>
            </div>
            <form onSubmit={handleSaveProduct} className="space-y-4 px-5 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Product Name *</label>
                <input type="text" value={newProduct.name} onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" autoFocus required />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
                <textarea value={newProduct.description} onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })} rows={3} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="Optional internal description" />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Rental Price *</label>
                  <input type="number" min="0" step="0.01" value={newProduct.price} onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" required />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Quantity *</label>
                  <input type="number" min="0" value={newProduct.quantity} onChange={(e) => setNewProduct({ ...newProduct, quantity: e.target.value })} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" required />
                </div>
              </div>
              <div className="flex flex-col-reverse gap-3 border-t border-gray-200 pt-4 sm:flex-row sm:justify-end">
                <button type="button" onClick={() => setShowProductModal(false)} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" disabled={savingProduct}>
                  Cancel
                </button>
                <button type="submit" className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60" disabled={savingProduct}>
                  {savingProduct ? "Saving..." : "Save Product"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderForm;
